interface SelectAllControlState {
    total: number;
    isChecked: boolean;
    isIndeterminate: boolean;
    action: "selectAll" | "clear";
}

/**
 * Derives the "select all matching" header state from the current selection
 * alone — independent of how many rows/pages are loaded. This is the crux of
 * the PRO-9847 fix: the header reflects the full server-side match set, so a
 * user can select all matching transactions without scrolling every page into
 * memory.
 *
 * Returns `undefined` when there is no meaningful all-matching control to show
 * (no known match total, or the applied-transactions view), in which case the
 * header falls back to TanStack's page-row behavior.
 */
export function resolveSelectAllControl(opts: {
    applyToExistingTransactions: boolean;
    includedTransactionsIds: number[];
    excludedTransactionsIds: number[];
    matchingTotal: number | undefined;
    isShowingApplied: boolean;
}): SelectAllControlState | undefined {
    if (opts.matchingTotal == null || opts.isShowingApplied) {
        return undefined;
    }

    const isAllMatchingSelected =
        opts.applyToExistingTransactions &&
        opts.includedTransactionsIds.length === 0 &&
        opts.excludedTransactionsIds.length === 0;
    const isSubsetSelected =
        opts.applyToExistingTransactions &&
        (opts.includedTransactionsIds.length > 0 ||
            opts.excludedTransactionsIds.length > 0);

    return {
        total: opts.matchingTotal,
        isChecked: isAllMatchingSelected,
        isIndeterminate: isSubsetSelected,
        action: isAllMatchingSelected ? "clear" : "selectAll",
    };
}

type RowSelectionResult =
    | { kind: "clear" }
    | { kind: "included"; ids: number[] }
    | { kind: "excluded"; ids: number[] };

/**
 * Resolves what a row-level selection change means. Individual row toggles
 * never escalate to all-matching (there is no `all` kind) — all-matching is
 * expressed exclusively via the header control. A hand-picked subset always
 * maps to exactly those `included` ids; narrowing from all-matching maps to
 * `excluded` ids. Deselecting everything clears the selection.
 */
export function resolveRowSelection(opts: {
    selectedIds: number[];
    excludedIds: number[];
    applyToExistingTransactions: boolean;
    includedTransactionsIds: number[];
}): RowSelectionResult {
    if (opts.selectedIds.length === 0) {
        return { kind: "clear" };
    }

    // Not yet applying to all, or already tracking an explicit included subset:
    // the selection is exactly the checked rows.
    if (
        !opts.applyToExistingTransactions ||
        opts.includedTransactionsIds.length > 0
    ) {
        return { kind: "included", ids: opts.selectedIds };
    }

    // Already in all-matching mode (apply-to-all, no explicit include set):
    // unchecking rows narrows the set via exclusions.
    return { kind: "excluded", ids: opts.excludedIds };
}
